import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, ShoppingCart, ChevronRight, ChevronLeft, Bell, Zap, Star, Sparkles, TrendingUp, Shirt, Heart, Smartphone, Baby, BookOpen, Filter, X } from 'lucide-react';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { CATEGORIES, PRODUCTS, MERCHANTS, getFeaturedProducts, getTopSellingProducts, getProductsByCategory, getMerchantsByCategory } from '../../data/marketplaceData';
import ProductCard from '../marketplace/ProductCard';
import ProductDetailDrawer from '../marketplace/ProductDetailDrawer';
import CartDrawer from '../marketplace/CartDrawer';
import CheckoutDrawer from '../marketplace/CheckoutDrawer';

// Category icon mapping
const CATEGORY_ICONS = {
  fashion: Shirt,
  healthcare: Heart,
  technology: Smartphone,
  baby: Baby,
  education: BookOpen,
};

export default function MarketplaceTab() {
  const { isDesktop } = useResponsive();
  const { user } = useAuth();
  const { setShowNotifications, unreadCount, profileForm } = useAppState();
  const {
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    cart,
    setShowCart,
    getCartTotals,
  } = useMarketplace();

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [showFilters, setShowFilters] = useState(false);

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const userFirstName = user?.first_name || profileForm?.firstName || 'there';

  const cartTotals = getCartTotals();

  // Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(localSearch);
  };

  // Clear search
  const handleClearSearch = () => {
    setLocalSearch('');
    setSearchQuery('');
  };

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    let products = [...PRODUCTS];

    if (selectedCategory) {
      products = products.filter(p => p.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      products = products.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query) ||
        p.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    return products;
  }, [selectedCategory, searchQuery]);

  // Get data for different sections
  const featuredProducts = getFeaturedProducts();
  const topSelling = getTopSellingProducts(8);
  const flashDeals = PRODUCTS.filter(p => p.discount >= 25).slice(0, 8);

  // Check if showing filtered results
  const isFiltered = searchQuery || selectedCategory;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Mobile Header */}
        {!isDesktop && (
          <header className="sticky top-0 bg-white z-10 px-4 pb-3 border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
                <h1 className="font-bold text-gray-900 text-lg">
                  {getGreeting()}, {userFirstName}
                </h1>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCart(true)}
                  className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
                >
                  <ShoppingCart size={22} />
                  {cartTotals.itemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {cartTotals.itemCount > 9 ? '9+' : cartTotals.itemCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setShowNotifications(true)}
                  className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
                >
                  <Bell size={22} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 rounded-full" />
                  )}
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder="Search products, brands..."
                className="w-full pl-12 pr-12 py-3 bg-gray-100 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {localSearch && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              )}
            </form>
          </header>
        )}

        {/* Desktop Search Bar */}
        {isDesktop && (
          <div className="px-6 py-4 bg-white border-b border-gray-100">
            <div className="max-w-4xl mx-auto flex items-center gap-4">
              <form onSubmit={handleSearch} className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  placeholder="Search products, brands, categories..."
                  className="w-full pl-12 pr-12 py-3 bg-gray-100 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {localSearch && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                )}
              </form>
              <button
                onClick={() => setShowCart(true)}
                className="relative p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
              >
                <ShoppingCart size={22} />
                {cartTotals.itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {cartTotals.itemCount > 9 ? '9+' : cartTotals.itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        <div className={`px-4 py-6 ${isDesktop ? 'max-w-6xl mx-auto' : ''}`}>
          {isFiltered ? (
            /* Filtered Results View */
            <FilteredResultsView
              products={filteredProducts}
              searchQuery={searchQuery}
              selectedCategory={selectedCategory}
              onClearCategory={() => setSelectedCategory(null)}
              onClearSearch={handleClearSearch}
            />
          ) : (
            /* Main Marketplace View */
            <>
              {/* Categories Navigation */}
              <CategoriesSection
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
              />

              {/* Hero Carousel */}
              <HeroCarousel products={featuredProducts.slice(0, 5)} />

              {/* Flash Deals */}
              {flashDeals.length > 0 && (
                <ProductCarousel
                  title="Flash Deals"
                  subtitle="Up to 55% off"
                  icon={<Zap className="text-amber-500" size={20} />}
                  products={flashDeals}
                  accentColor="amber"
                />
              )}

              {/* Featured Products */}
              <ProductCarousel
                title="Featured Products"
                subtitle="Handpicked for you"
                icon={<Sparkles className="text-indigo-500" size={20} />}
                products={featuredProducts}
                accentColor="indigo"
              />

              {/* Top Selling */}
              <ProductCarousel
                title="Best Sellers"
                subtitle="What customers love"
                icon={<TrendingUp className="text-emerald-500" size={20} />}
                products={topSelling}
                accentColor="emerald"
              />

              {/* Shop by Category */}
              {CATEGORIES.map(category => (
                <CategorySection
                  key={category.id}
                  category={category}
                  onViewAll={() => setSelectedCategory(category.id)}
                />
              ))}
            </>
          )}
        </div>
      </div>

      {/* Drawers */}
      <ProductDetailDrawer />
      <CartDrawer />
      <CheckoutDrawer />
    </div>
  );
}

// Categories Navigation
function CategoriesSection({ selectedCategory, onSelectCategory }) {
  return (
    <section className="mb-6">
      <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-2">
        <button
          onClick={() => onSelectCategory(null)}
          className={`flex-shrink-0 flex flex-col items-center gap-2 p-3 rounded-2xl transition-all ${
            !selectedCategory
              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
              : 'bg-white text-gray-600 border border-gray-100 hover:border-indigo-200'
          }`}
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            !selectedCategory ? 'bg-white/20' : 'bg-gray-100'
          }`}>
            <Sparkles size={24} className={!selectedCategory ? 'text-white' : 'text-indigo-600'} />
          </div>
          <span className="text-xs font-bold whitespace-nowrap">All</span>
        </button>
        {CATEGORIES.map(category => {
          const Icon = CATEGORY_ICONS[category.id] || Sparkles;
          const isActive = selectedCategory === category.id;
          return (
            <button
              key={category.id}
              onClick={() => onSelectCategory(category.id)}
              className={`flex-shrink-0 flex flex-col items-center gap-2 p-3 rounded-2xl transition-all ${
                isActive
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                  : 'bg-white text-gray-600 border border-gray-100 hover:border-indigo-200'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isActive ? 'bg-white/20' : category.color + '/10'
              }`}>
                <Icon size={24} className={isActive ? 'text-white' : category.color.replace('bg-', 'text-')} />
              </div>
              <span className="text-xs font-bold whitespace-nowrap">{category.name}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// Hero Carousel
function HeroCarousel({ products }) {
  const scrollRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { setSelectedProduct, trackProductView } = useMarketplace();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % products.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [products.length]);

  useEffect(() => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.offsetWidth;
      scrollRef.current.scrollTo({
        left: currentIndex * cardWidth,
        behavior: 'smooth',
      });
    }
  }, [currentIndex]);

  const handleProductClick = (product) => {
    trackProductView(product.id);
    setSelectedProduct(product);
  };

  if (products.length === 0) return null;

  return (
    <section className="mb-8 -mx-4">
      <div
        ref={scrollRef}
        className="overflow-x-auto scrollbar-hide snap-x snap-mandatory"
      >
        <div className="flex">
          {products.map((product, idx) => (
            <button
              key={product.id}
              onClick={() => handleProductClick(product)}
              className="flex-shrink-0 w-full px-4 snap-center"
            >
              <div className="relative h-48 md:h-64 rounded-2xl overflow-hidden bg-gradient-to-r from-indigo-600 to-purple-600">
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-indigo-600/80 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-center">
                  <span className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-3 py-1 rounded-full w-fit mb-3">
                    <Zap size={12} />
                    {product.discount}% OFF
                  </span>
                  <h2 className="text-white text-xl md:text-2xl font-black mb-2 line-clamp-2 max-w-[60%]">
                    {product.name}
                  </h2>
                  <div className="flex items-baseline gap-2">
                    <span className="text-white text-2xl font-black">${product.price.toFixed(2)}</span>
                    <span className="text-white/60 text-lg line-through">${product.originalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Dots */}
      <div className="flex justify-center gap-2 mt-4">
        {products.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentIndex ? 'bg-indigo-600 w-6' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </section>
  );
}

// Product Carousel
function ProductCarousel({ title, subtitle, icon, products, accentColor }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, [products]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'right' ? 280 : -280;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  if (products.length === 0) return null;

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <h3 className="font-black text-lg text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="p-2 bg-gray-100 rounded-full text-gray-600 hover:bg-gray-200"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className={`p-2 bg-${accentColor}-100 rounded-full text-${accentColor}-600 hover:bg-${accentColor}-200`}
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
      <div
        ref={scrollRef}
        className="overflow-x-auto scrollbar-hide -mx-4 px-4"
      >
        <div className="flex gap-4" style={{ width: 'max-content' }}>
          {products.map(product => (
            <div key={product.id} className="w-44 md:w-52 flex-shrink-0">
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Category Section with Products
function CategorySection({ category, onViewAll }) {
  const products = getProductsByCategory(category.id).slice(0, 4);
  const merchants = getMerchantsByCategory(category.id);
  const Icon = CATEGORY_ICONS[category.id] || Sparkles;

  if (products.length === 0) return null;

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 ${category.color} rounded-xl flex items-center justify-center`}>
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-black text-lg text-gray-900">{category.name}</h3>
            <p className="text-xs text-gray-500">{merchants.length} merchants · {getProductsByCategory(category.id).length} products</p>
          </div>
        </div>
        <button
          onClick={onViewAll}
          className="flex items-center gap-1 text-indigo-600 text-sm font-bold hover:text-indigo-700"
        >
          View All
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {products.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </section>
  );
}

// Filtered Results View
function FilteredResultsView({ products, searchQuery, selectedCategory, onClearCategory, onClearSearch }) {
  const category = CATEGORIES.find(c => c.id === selectedCategory);

  return (
    <div>
      {/* Active Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-sm text-gray-500">Showing {products.length} results</span>
        {searchQuery && (
          <button
            onClick={onClearSearch}
            className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1.5 rounded-full hover:bg-indigo-200"
          >
            "{searchQuery}"
            <X size={14} />
          </button>
        )}
        {selectedCategory && category && (
          <button
            onClick={onClearCategory}
            className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1.5 rounded-full hover:bg-indigo-200"
          >
            {category.name}
            <X size={14} />
          </button>
        )}
      </div>

      {/* Results Grid */}
      {products.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Search size={32} className="text-gray-400" />
          </div>
          <h3 className="font-bold text-gray-900 text-lg mb-2">No products found</h3>
          <p className="text-gray-500 text-sm mb-6">Try adjusting your search or filters</p>
          <button
            onClick={() => {
              onClearSearch();
              onClearCategory();
            }}
            className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700"
          >
            Clear All Filters
          </button>
        </div>
      )}
    </div>
  );
}
